import { createParams } from "../services/odata.service";
import {
  AgentAllotment,
  AgentAllotmentItem,
  AgentDossier,
  AgentInvoiceItem,
  AgentPrintAction,
  AgentPrintActionRequest,
  ChangeEntryAgentSurchargeAction,
  Column,
  CustomEntryStatusItem,
  DossierViewResult,
  EntryTotals,
  FilterItem,
  NumberStringPair,
  PageResult,
  PrintActionGroup,
  TideClientConfig,
} from "../types";
import { AgentDossierItem } from "../types/web/agent-dossier-item";
import { post } from "./api";
import { get } from "./common-client";

const ENDPOINT = "/api/web/agent";
const ENDPOINT_GET_PRINT_ACTIONS = `${ENDPOINT}/print-action`;
const ENDPOINT_PRINT = `${ENDPOINT}/print-action`;
const ENDPOINT_CUSTOM_ENTRY_STATUS = `${ENDPOINT}/custom-entry-status`;
const ENDPOINT_ENUM_ENTRY_STATUS = `${ENDPOINT}/enum/entry-status`;
const ENDPOINT_CHANGE_ENTRY_AGENT_SURCHARGE = `${ENDPOINT}/entry/change-agent-surcharge`;

/**
 * api/web/agent/print-action/{print-action-group}
 * @param config
 * @param printActionGroup
 * @param signal
 * @returns print actions that are available for agents for given group.
 */
export const getPrintActions = (
  config: TideClientConfig,
  printActionGroup: PrintActionGroup,
  signal?: AbortSignal
): Promise<AgentPrintAction[]> => {
  const url = `${config.host}${ENDPOINT_GET_PRINT_ACTIONS}/${printActionGroup}`;
  const apiKey = config.apiKey;

  return get<AgentPrintAction[]>(url, apiKey, config.token, signal, true);
};

/**
 * api/web/agent/print-action
 * @param config
 * @param request
 * @param signal
 * @returns pdf as stream.
 */
export const print = (
  config: TideClientConfig,
  request: AgentPrintActionRequest,
  signal?: AbortSignal
): Promise<Response> => {
  const url = `${config.host}${ENDPOINT_PRINT}`;
  const apiKey = config.apiKey;
  const body = JSON.stringify(request);

  return post(url, apiKey, body, config.token, signal, undefined);
};

export const getEntryList = (
  config: TideClientConfig,
  filterItem: FilterItem,
  gridColumns?: Column[],
  signal?: AbortSignal
): Promise<PageResult<DossierViewResult>> => {
  const params = createParams(filterItem, false, gridColumns);
  const queryString = Object.keys(params)
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )
    .join("&");
  const url = `${config.host}${ENDPOINT}/entry/list?${queryString}`;
  const apiKey = config.apiKey;

  return get<PageResult<DossierViewResult>>(
    url,
    apiKey,
    config.token,
    signal,
    true
  );
};

export const getEntryListv2 = (
  config: TideClientConfig,
  filterItem: FilterItem,
  gridColumns?: Column[],
  signal?: AbortSignal
): Promise<PageResult<AgentDossierItem>> => {
  const params = createParams(filterItem, false, gridColumns);
  const queryString = Object.keys(params)
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )
    .join("&");
  const url = `${config.host}${ENDPOINT}/v2/entry/list?${queryString}`;
  const apiKey = config.apiKey;

  return get<PageResult<AgentDossierItem>>(
    url,
    apiKey,
    config.token,
    signal,
    true
  );
};

export const getEntryTotals = (
  config: TideClientConfig,
  filterItem: FilterItem,
  gridColumns?: Column[],
  signal?: AbortSignal
): Promise<EntryTotals> => {
  const params = createParams(filterItem, false, gridColumns);
  const queryString = Object.keys(params)
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )
    .join("&");
  const url = `${config.host}${ENDPOINT}/entry/total?${queryString}`;
  const apiKey = config.apiKey;

  return get<EntryTotals>(url, apiKey, config.token, signal, true);
};

export const getEntryDetailv2 = (
  config: TideClientConfig,
  entryId: number,
  signal?: AbortSignal
): Promise<AgentDossier> => {
  const url = `${config.host}${ENDPOINT}/v2/entry/detail/${entryId}`;
  const apiKey = config.apiKey;

  return get<AgentDossier>(url, apiKey, config.token, signal, true);
};

export const getInvoiceList = (
  config: TideClientConfig,
  filterItem: FilterItem,
  gridColumns?: Column[],
  signal?: AbortSignal
): Promise<PageResult<AgentInvoiceItem>> => {
  const params = createParams(filterItem, false, gridColumns);
  const queryString = Object.keys(params)
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )
    .join("&");
  const url = `${config.host}${ENDPOINT}/invoice/list?${queryString}`;
  const apiKey = config.apiKey;

  return get<PageResult<AgentInvoiceItem>>(
    url,
    apiKey,
    config.token,
    signal,
    true
  );
};

export const getCustomEntryStatus = (
  config: TideClientConfig,
  signal?: AbortSignal,
  languageCode?: string
): Promise<CustomEntryStatusItem[]> => {
  const url = `${config.host}${ENDPOINT_CUSTOM_ENTRY_STATUS}`;
  const apiKey = config.apiKey;

  return get<CustomEntryStatusItem[]>(
    url,
    apiKey,
    config.token,
    signal,
    true,
    languageCode
  );
};

export const getEntryStatus = (
  config: TideClientConfig,
  signal?: AbortSignal,
  languageCode?: string
): Promise<NumberStringPair[]> => {
  const url = `${config.host}${ENDPOINT_ENUM_ENTRY_STATUS}`;
  const apiKey = config.apiKey;

  return get<NumberStringPair[]>(
    url,
    apiKey,
    config.token,
    signal,
    true,
    languageCode
  );
};

export const changeEntryAgentSurcharge = (
  config: TideClientConfig,
  request: ChangeEntryAgentSurchargeAction,
  signal?: AbortSignal
) => {
  const url = `${config.host}${ENDPOINT_CHANGE_ENTRY_AGENT_SURCHARGE}`;
  const apiKey = config.apiKey;
  const body = JSON.stringify(request);

  return post(url, apiKey, body, config.token, signal);
};

export const getAllotmentList = (
  config: TideClientConfig,
  filterItem: FilterItem,
  gridColumns?: Column[],
  signal?: AbortSignal
): Promise<PageResult<AgentAllotmentItem>> => {
  const params = createParams(filterItem, false, gridColumns);
  const queryString = Object.keys(params)
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`
    )
    .join("&");
  const url = `${config.host}${ENDPOINT}/allotment/list?${queryString}`;
  const apiKey = config.apiKey;

  return get<PageResult<AgentAllotmentItem>>(
    url,
    apiKey,
    config.token,
    signal,
    true
  );
};

export const getAllotment = (
  config: TideClientConfig,
  allotmentId: number,
  signal?: AbortSignal
): Promise<AgentAllotment> => {
  const url = `${config.host}${ENDPOINT}/allotment/${allotmentId}`;
  const apiKey = config.apiKey;

  return get<AgentAllotment>(url, apiKey, config.token, signal, true);
};
